/* ==========================================================================
   FRAMEWRK enhance.js  — site-wide UI behavior layer
   Save as: assets/framewrk-enhance.js
   Include via layout/theme.liquid: {{ 'framewrk-enhance.js' | asset_url | script_tag }}
   ========================================================================== */

(() => {
  'use strict';

  /* ---------------------------------------------------------------------
     1. Scroll-reveal observer — generic data-fw-reveal hook
     --------------------------------------------------------------------- */
  const reveal = () => {
    const els = document.querySelectorAll('[data-fw-reveal]:not(.fw-on)');
    if (!els.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('fw-on');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(el => io.observe(el));
  };

  /* ---------------------------------------------------------------------
     2. Sticky add-to-cart bar (auto-injects on product pages)
     --------------------------------------------------------------------- */
  const stickyATC = () => {
    if (!document.body.classList.contains('template-product') && !document.querySelector('[data-product-form]') && !window.location.pathname.includes('/products/')) return;
    const form = document.querySelector('form[action*="/cart/add"], .product-form__submit, [data-product-form] button[type="submit"]');
    const submitBtn = document.querySelector('.product-form__submit, button[name="add"], .shopify-payment-button__button');
    if (!form || !submitBtn) return;
    if (document.querySelector('.fw-sticky-atc')) return;

    const titleEl = document.querySelector('.product__title, h1.product-title, .product-single__title, h1');
    const priceEl = document.querySelector('.product .price, .price__regular .price-item, [data-product-price]');
    const title = (titleEl ? titleEl.textContent.trim() : 'Add to cart');
    const price = (priceEl ? priceEl.textContent.trim() : '');

    const bar = document.createElement('div');
    bar.className = 'fw-sticky-atc';
    bar.innerHTML = `
      <div class="fw-sticky-atc__l">
        <div class="fw-sticky-atc__title">${title.slice(0, 60)}</div>
        <div class="fw-sticky-atc__price">${price}</div>
      </div>
      <button type="button" class="fw-sticky-atc__cta">ADD TO CART  →</button>
    `;
    document.body.appendChild(bar);
    bar.querySelector('.fw-sticky-atc__cta').addEventListener('click', () => {
      submitBtn.click();
    });

    if (!('IntersectionObserver' in window)) { bar.classList.add('visible'); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        bar.classList.toggle('visible', !e.isIntersecting);
      });
    }, { rootMargin: '-120px 0px 0px 0px' });
    io.observe(submitBtn);
  };

  /* ---------------------------------------------------------------------
     3. Magnetic button hover (subtle pull toward cursor)
     --------------------------------------------------------------------- */
  const magneticButtons = () => {
    const btns = document.querySelectorAll('.fw-btn--magnetic, .button.button--magnetic');
    if (!btns.length || window.matchMedia('(pointer: coarse)').matches) return;
    btns.forEach(btn => {
      btn.addEventListener('mousemove', (e) => {
        const r = btn.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2;
        const y = e.clientY - r.top - r.height / 2;
        btn.style.transform = `translate(${x * 0.18}px, ${y * 0.18}px)`;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
      });
    });
  };

  /* ---------------------------------------------------------------------
     4. Header — shrink on scroll
     --------------------------------------------------------------------- */
  const headerScroll = () => {
    const h = document.querySelector('.header, sticky-header, header.section-header');
    if (!h) return;
    let lastY = 0;
    const onScroll = () => {
      const y = window.scrollY;
      h.classList.toggle('fw-header--scrolled', y > 12);
      h.classList.toggle('fw-header--hidden', y > 180 && y > lastY);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  };

  /* ---------------------------------------------------------------------
     5. Smooth in-page anchor scroll
     --------------------------------------------------------------------- */
  const smoothAnchor = () => {
    document.querySelectorAll('a[href^="#"]').forEach(a => {
      a.addEventListener('click', (e) => {
        const href = a.getAttribute('href');
        if (!href || href.length < 2) return;
        const target = document.querySelector(href);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  /* ---------------------------------------------------------------------
     6. Parallax on hero background (subtle vertical shift)
     --------------------------------------------------------------------- */
  const parallax = () => {
    const target = document.querySelector('.fw-hero__bg, .banner__media');
    if (!target) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        target.style.transform = `translate3d(0, ${y * 0.18}px, 0)`;
        raf = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  };

  /* ---------------------------------------------------------------------
     7. Cart count pulse on update
     --------------------------------------------------------------------- */
  const cartPulse = () => {
    const bubble = document.querySelector('.cart-count-bubble, [data-cart-count]');
    if (!bubble) return;
    let last = bubble.textContent;
    new MutationObserver(() => {
      if (bubble.textContent !== last) {
        bubble.classList.add('fw-pulse');
        setTimeout(() => bubble.classList.remove('fw-pulse'), 700);
        last = bubble.textContent;
      }
    }).observe(bubble, { childList: true, subtree: true, characterData: true });
  };

  /* ---------------------------------------------------------------------
     8. ATC microcopy — change button label briefly on click for confidence
     --------------------------------------------------------------------- */
  const atcMicrocopy = () => {
    const btn = document.querySelector('.product-form__submit, [data-add-to-cart], button[name="add"]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const orig = btn.innerHTML;
      setTimeout(() => {
        if (btn.classList.contains('loading')) return;
        btn.innerHTML = '<span>ADDED ✓</span>';
        setTimeout(() => { btn.innerHTML = orig; }, 1800);
      }, 600);
    });
  };

  /* ---------------------------------------------------------------------
     9. Lazy load video for hero
     --------------------------------------------------------------------- */
  const lazyVideo = () => {
    document.querySelectorAll('video[data-fw-lazy]').forEach(v => {
      const src = v.getAttribute('data-fw-lazy');
      if (src) v.src = src;
      v.play().catch(() => {});
    });
  };

  /* ---------------------------------------------------------------------
     10. Init everything on DOMContentLoaded
     --------------------------------------------------------------------- */
  const boot = () => {
    reveal();
    stickyATC();
    magneticButtons();
    headerScroll();
    smoothAnchor();
    parallax();
    cartPulse();
    atcMicrocopy();
    lazyVideo();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init reveal observer when Shopify section editor reloads a section
  document.addEventListener('shopify:section:load', reveal);
  document.addEventListener('shopify:section:reorder', reveal);

})();